import type { Permission } from '../types';

export const SETTINGS_SECTION_PERMISSION: Record<string, string> = {
  locations: 'settings_locations',
  positions: 'settings_positions',
  schedules: 'settings_schedules',
  'payroll-departments': 'settings_payroll_departments',
  'shift-templates': 'settings_shift_templates',
  'time-stations': 'time_station_clock_in_out',
  roles: 'settings_roles',
  'attendance-settings': 'settings_attendance',
  'working-time-rest': 'settings_working_time_rest',
  'time-cards': 'settings_time_cards',
  'audit-logs': 'settings_audit_logs',
  'feature-access': 'settings_feature_access',
  'nurse-banding': 'settings_position_banding',
  'training-types': 'settings_training_types',
  'company-settings': 'settings_company',
  'public-holidays': 'settings_public_holidays',
  'face-sync-status': 'settings_face_sync',
};

export const SETTINGS_SECTION_ORDER = Object.keys(SETTINGS_SECTION_PERMISSION);

export const REPORT_SECTION_PERMISSION: Record<string, string> = {
  'plan-vs-actual': 'reports_plan_vs_actual',
  'audit-logs': 'reports_audit_logs',
  'payroll-builder': 'reports_payroll',
  'timecards-analysis': 'reports_timecard_analysis',
  'timecards-weekly-totals': 'reports_weekly_totals',
  'timecards-early-late': 'reports_early_late',
  'timecards-break': 'reports_break',
  'timecards-staff-salaried': 'reports_staff_salaried',
  'employee-details': 'reports_employee_details',
  'schedules-invoice': 'reports_invoice',
  'leave-ledger': 'reports_leave_ledger',
};

export const TIMECARD_REPORT_SECTIONS = [
  'timecards-analysis',
  'timecards-weekly-totals',
  'timecards-early-late',
  'timecards-break',
  'timecards-staff-salaried',
];

export const CUSTOM_REPORT_TYPE_PERMISSION: Record<string, string> = {
  LateArrivals: 'reports_late_arrivals',
  ClockAnomalies: 'reports_clock_anomalies',
  CertifiedSickLeave: 'reports_certified_sick_leave',
  Overtime48: 'reports_overtime',
  Probation: 'reports_probation',
  JoinersLeavers: 'reports_joiners_leavers',
  ContractExpiry: 'reports_contract_expiry',
  Compliance: 'reports_compliance',
  Birthdays: 'reports_birthdays',
  AuditLog: 'reports_custom_audit_log',
};

type HasPermission = (code: string) => boolean;

export function canOpenSettingsSection(sectionId: string, hasPermission: HasPermission): boolean {
  if (hasPermission('settings')) return true;
  const code = SETTINGS_SECTION_PERMISSION[sectionId];
  return Boolean(code) && hasPermission(code);
}

export function hasSettingsMenu(hasPermission: HasPermission): boolean {
  return hasPermission('settings') || SETTINGS_SECTION_ORDER.some((id) => canOpenSettingsSection(id, hasPermission));
}

export function canOpenReportSection(sectionId: string, hasPermission: HasPermission): boolean {
  if (hasPermission('reports')) return true;
  if (sectionId.startsWith('timecards') && hasPermission('reports_timecards')) return true;
  if (sectionId === 'audit-logs') {
    return (
      hasPermission('reports_audit_logs') ||
      hasPermission('settings') ||
      hasPermission('settings_audit_logs')
    );
  }
  const code = REPORT_SECTION_PERMISSION[sectionId];
  return Boolean(code) && hasPermission(code);
}

export function canOpenCustomReportType(reportType: string, hasPermission: HasPermission): boolean {
  if (hasPermission('reports') || hasPermission('reports_custom')) return true;
  const code = CUSTOM_REPORT_TYPE_PERMISSION[reportType];
  return Boolean(code) && hasPermission(code);
}

export function hasCustomReports(hasPermission: HasPermission): boolean {
  return (
    hasPermission('reports') ||
    hasPermission('reports_custom') ||
    Object.values(CUSTOM_REPORT_TYPE_PERMISSION).some((code) => hasPermission(code))
  );
}

export function hasTimecardReports(hasPermission: HasPermission): boolean {
  return (
    hasPermission('reports_timecards') ||
    TIMECARD_REPORT_SECTIONS.some((id) => canOpenReportSection(id, hasPermission))
  );
}

export function hasReportsMenu(hasPermission: HasPermission): boolean {
  return (
    hasPermission('reports') ||
    hasCustomReports(hasPermission) ||
    hasTimecardReports(hasPermission) ||
    Object.keys(REPORT_SECTION_PERMISSION).some((id) => canOpenReportSection(id, hasPermission))
  );
}

export interface PermissionNode {
  permission: Permission;
  children: PermissionNode[];
}

export function buildPermissionTree(permissions: Permission[]): PermissionNode[] {
  const byParent = new Map<number | null, Permission[]>();
  const ids = new Set(permissions.map((p) => p.id));
  for (const permission of permissions) {
    const parentId = permission.parentId != null && ids.has(permission.parentId) ? permission.parentId : null;
    const list = byParent.get(parentId) ?? [];
    list.push(permission);
    byParent.set(parentId, list);
  }

  const sort = (list: Permission[]) =>
    [...list].sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));

  const build = (parentId: number | null): PermissionNode[] =>
    sort(byParent.get(parentId) ?? []).map((permission) => ({
      permission,
      children: build(permission.id),
    }));

  return build(null);
}

export function descendantIds(node: PermissionNode): number[] {
  return node.children.flatMap((child) => [child.permission.id, ...descendantIds(child)]);
}

function storedToChecked(nodes: PermissionNode[], stored: Set<number>): Set<number> {
  const checked = new Set<number>();
  const walk = (node: PermissionNode, ancestorOn: boolean) => {
    const on = ancestorOn || stored.has(node.permission.id);
    if (on) checked.add(node.permission.id);
    node.children.forEach((child) => walk(child, on));
  };
  nodes.forEach((node) => walk(node, false));
  return checked;
}

function collapse(nodes: PermissionNode[], checked: Set<number>): number[] {
  const stored: number[] = [];

  const walk = (node: PermissionNode): 'all' | 'some' | 'none' => {
    if (node.children.length === 0) {
      if (checked.has(node.permission.id)) {
        stored.push(node.permission.id);
        return 'all';
      }
      return 'none';
    }

    const states = node.children.map(walk);
    if (states.every((state) => state === 'all')) {
      const covered = new Set(descendantIds(node));
      for (let i = stored.length - 1; i >= 0; i -= 1) {
        if (covered.has(stored[i])) stored.splice(i, 1);
      }
      stored.push(node.permission.id);
      return 'all';
    }
    if (states.every((state) => state === 'none')) return 'none';
    return 'some';
  };

  nodes.forEach(walk);
  return stored;
}

export function normalizeStoredPermissionIds(permissions: Permission[], storedIds: number[]): number[] {
  const tree = buildPermissionTree(permissions);
  return collapse(tree, storedToChecked(tree, new Set(storedIds)));
}

export function togglePermissionNode(
  permissions: Permission[],
  storedIds: number[],
  node: PermissionNode,
  turnOn: boolean,
): number[] {
  const tree = buildPermissionTree(permissions);
  const checked = storedToChecked(tree, new Set(storedIds));
  const ids = [node.permission.id, ...descendantIds(node)];
  ids.forEach((id) => {
    if (turnOn) checked.add(id);
    else checked.delete(id);
  });
  return collapse(tree, checked);
}

export type PermissionCheckState = 'on' | 'off' | 'partial';

export function permissionCheckState(node: PermissionNode, checked: Set<number>): PermissionCheckState {
  if (node.children.length === 0) return checked.has(node.permission.id) ? 'on' : 'off';
  const childStates = node.children.map((child) => permissionCheckState(child, checked));
  if (childStates.every((state) => state === 'on')) return 'on';
  if (childStates.every((state) => state === 'off')) return 'off';
  return 'partial';
}

export function checkedPermissionIds(permissions: Permission[], storedIds: number[]): Set<number> {
  const tree = buildPermissionTree(permissions);
  return storedToChecked(tree, new Set(storedIds));
}
